import { create } from 'zustand';

/** permission の scope。API 側の `PERMISSION_SCOPES` (apps/api/src/lib/permission-constants.ts) と
 *  同じ集合。⚠️ 値を増やすときは両方を同時に更新すること (§8-93 型の二重定義)。 */
export type PermissionScope =
  | 'platform' | 'all_stores' | 'multi_store' | 'own_store' | 'node_scope' | 'self';

export type EffectivePermission = {
  feature: string;
  action: string;
  /** v0.4 追加要件⑱ D-446 (2026-09-02): `/auth/me` が返すようになった。
   *  ⚠️ **optional にしてあるのは、古いレスポンスを保持したクライアントでも壊れないようにするため**。
   *  未設定として扱う側の既定は、それを消費する箇所 (lib/navigation.ts の minScope 等) で
   *  **明示的に決めること** (黙って「全許可」に倒さない)。
   *  ⚠️ storeIds / nodeIds は API 側で意図的に返していない (組織構造の詳細を露出しないため)。 */
  scope?: PermissionScope;
};

export type CurrentUser = {
  user_id: string;
  tenant_id: string | null;
  name: string;
  // v0.3 (docs/design/v03-user-employee-refinement.md §2-4): display_name分離方針。
  // ログインレスポンスには含まれないため、ログイン直後はApp.tsxの/me再取得までundefinedの可能性がある
  display_name?: string;
  family_name?: string | null;
  given_name?: string | null;
  middle_name?: string | null;
  email: string | null;   // v0.2 で任意化
  login_id: string;       // v0.2 で必須化
  roles: string[];
  stores: string[];
  // v0.3 (S-14/S-16): サイドバー表示制御用。GET /auth/me が常に返す (login レスポンスには
  // 含まれないため、ログイン直後はApp.tsxの/me再取得まで undefined の可能性がある)
  effective_permissions?: EffectivePermission[];
  // v0.3 (S-1データ公開ゲート): GET /auth/me が常に返す。同上の理由でログイン直後は undefined の可能性
  // v0.4 追加要件⑪ 章③ (D-355): shift_report を追加 (GET /shift-report/daily nav の requiredTenantFeature 用)
  // v0.4 追加要件⑪ 章⑥ (D-349): overtime_compliance を追加 (GET /shift-report/compliance nav 用)
  tenant_features?: {
    publish_gate_enabled: boolean; shift_report?: boolean; overtime_compliance?: boolean; document_library?: boolean;
    // v0.4 休暇管理 (TD-26) フロントエンド実装: nav の requiredTenantFeature 判定に使う
    leave_management?: boolean; family_member_management?: boolean;
    // 人・組織機能 増分② (D-608): SC-01〜SC-05 (人材像レイヤー本体)。nav の
    // requiredTenantFeature='competency_management' 判定に使う
    competency_management?: boolean;
    // 人・組織機能 増分③ (D-608): SC-07 (配置制約)。nav の
    // requiredTenantFeature='placement_constraint' 判定に使う
    placement_constraint?: boolean;
    // 人・組織機能 増分⑦ (D-608): SC-10 (育成計画)。nav の
    // requiredTenantFeature='development_plan' 判定に使う
    development_plan?: boolean;
    // 5-1 ダッシュボード参照月・ノード固定機能 (docs/design/v05-dashboard-scope-pinning.md §0-2-3):
    // ScopePinToggle.tsx の表示可否判定に使う
    scope_pinning?: boolean;
    // v0.5⑧ 経営会議PDCA (§7-7): nav の requiredTenantFeature='board_meeting' 判定に使う
    board_meeting?: boolean;
    // v0.5 AIメニュー開発の利用ランキング: nav の requiredTenantFeature='ai_usage_management' 判定に使う
    // (既存フラグへの相乗り、design v05-ai-menu-usage-ranking-detail.md §3-5)
    ai_usage_management?: boolean;
    // v0.4後半 月次実績一括入力 第2フェーズ I-1a: AttendanceSettingsPage.tsx の予約UI表示可否判定に使う
    attendance_monthly_v2?: boolean;
  };
  // v1.0 (Phase 3 Sprint 11): テナントのedition ('lite'|'full')。login/me両方が返す
  // (docs/design/v10-multi-tenant.md Chapter 4.7、edition-awareなダッシュボード出し分けに使用)
  edition?: 'lite' | 'full';
  // v1.0 (Phase 4、docs/design/v10-phase4-platform-admin-ui.md §2-4): テナント切替UI・
  // システム管理者バナーの表示判定用。GET /auth/me が常に返す (login レスポンスには含まれないため、
  // ログイン直後はApp.tsxの/me再取得までundefinedの可能性がある、他のv0.3拡張フィールドと同型)
  active_tenant_id?: string | null;
  primary_tenant_id?: string | null;
  is_cross_tenant_admin?: boolean;
  // v0.4 章① (D-129 2巡目 platform N-2): compensation/payroll は API 側 (lib/permission-constants.ts
  // の NON_INHERITING_FEATURES) で feature:'*' からの継承と action 継承の両方を意図的に切っている。
  // GET /auth/me が常に返す (login レスポンスには含まれないため、ログイン直後は undefined の可能性)。
  // フロントが独自にハードコードすると §8-93 (二重定義の同期漏れ) と同型の乖離が起きるため共有する
  non_inheriting_features?: string[];
};

/** hasPermission の nonInheritingFeatures が未指定 (user オブジェクト非経由の呼び出し) の場合の
 *  フォールバック。API 側 lib/permission-constants.ts の NON_INHERITING_FEATURES と一致させること。 */
export const DEFAULT_NON_INHERITING_FEATURES = ['compensation', 'payroll'];

/** feature+action の実効権限を持つか判定する (フロントエンド版、'*'ワイルドカード・edit/manage互換込み)。
 *  apps/api/src/lib/scope.ts の hasPermissionForFeatureAction と対応するロジック。 */
export type PermissionAction =
  | 'view' | 'create' | 'update' | 'delete' | 'manage'
  | 'publish' | 'unpublish' | 'mark_not_applicable' | 'view_draft';

const ACCEPTED_ACTIONS: Record<string, string[]> = {
  view: ['view', 'edit', 'manage'],
  create: ['create', 'edit', 'manage'],
  update: ['update', 'edit', 'manage'],
  delete: ['delete', 'edit', 'manage'],
  manage: ['manage'],
  // v0.3 (S-1、Design H1対応): publish系はmanageから継承させない (バックエンドのACCEPTED_ACTIONSと対応)
  publish: ['publish'],
  unpublish: ['unpublish'],
  mark_not_applicable: ['mark_not_applicable'],
  view_draft: ['view_draft'],
};

/** feature+action に一致する permission 行を返す。
 *  apps/api/src/lib/scope.ts の `matchesPermission` と対応するロジック。
 *  ⚠️ `hasPermission` と `hasTenantWidePermission` が共有する唯一の絞り込み実装。
 *  片方だけに条件を足すと両者の判定が乖離するため、分岐はここに1本化すること。 */
const matchingPermissions = (
  permissions: EffectivePermission[],
  feature: string,
  action: PermissionAction,
  nonInheritingFeatures: string[],
): EffectivePermission[] => {
  const nonInheriting = nonInheritingFeatures.includes(feature);
  const acceptedActions = ACCEPTED_ACTIONS[action] ?? [action];
  return permissions.filter((p) => {
    const featureMatches = p.feature === feature || (p.feature === '*' && !nonInheriting);
    if (!featureMatches) return false;
    return nonInheriting ? p.action === action : acceptedActions.includes(p.action);
  });
};

export const hasPermission = (
  permissions: EffectivePermission[] | undefined,
  feature: string,
  action: PermissionAction,
  // v0.4 章① (D-129): 呼び出し元が user.non_inheriting_features を渡せる場合は渡すこと。
  // 渡されなければ API 側と一致するハードコード既定値にフォールバックする
  nonInheritingFeatures: string[] = DEFAULT_NON_INHERITING_FEATURES,
): boolean => {
  if (!permissions) return false;
  return matchingPermissions(permissions, feature, action, nonInheritingFeatures).length > 0;
};

/** 「テナント全体スコープ」とみなす scope の集合。
 *  ⚠️ apps/api/src/lib/scope.ts の `resolveScopeForFeatureAction` が `allStores: true` を返す条件
 *  (`p.scope === 'all_stores' || p.scope === 'platform'`) からの複製。
 *  両者の一致は apps/api/src/lib/__tests__/tenant-wide-scopes-sync.test.ts が機械検証する (§8-93 対策)。
 *  ⚠️⚠️ **順序 (広さの全順序) を持ち込まないこと** — `PERMISSION_SCOPES` は全順序ではなく
 *  `node_scope` と `own_store` は比較不能。`lib/navigation.ts` が `minScope` という名前を意識的に
 *  避けたのと同じ理由で、ここも集合への所属判定として扱い `gte` 系の比較関数を作らない。 */
export const TENANT_WIDE_SCOPES: PermissionScope[] = ['all_stores', 'platform'];

/**
 * v0.4 TD-22: feature+action を持ち、**かつその権限がテナント全体スコープである**かを判定する。
 *
 * ⚠️ **表示制御専用**。バックエンドの `requireTenantWideManage` (18 の route ファイルで独立に定義され
 * ているが意味論は同一) と同じ条件を UI 側で再現し、**押せば必ず 403 になるボタンを出さない**ためだけに
 * 使う。`own_store` / `node_scope` しか持たない店長にテナント全体マスタの編集ボタンを見せる UX 不整合
 * (TD-22) を塞ぐのが目的。
 *
 * ⚠️⚠️ **サーバー側の検証を省略する根拠にしてはならない。** この値はクライアントが保持する `/auth/me`
 * の応答に過ぎず改変可能であり、実際の認可は常にサーバー側の `requireTenantWideManage` が行う。
 *
 * ⚠️ `hasPermission()` の**代わりではなく**、より強い条件として単独で使う (本関数は feature+action の
 * 一致も内包している)。
 *
 * ⚠️ **差し替え単位は「画面」ではなく「ボタン ↔ そのボタンが叩くエンドポイント」**。同一画面でも
 * バックエンドの守りが混在する例がある (例: `StoreEventsPage` はイベント種別が全店舗要求、イベント登録は
 * `own_store` で正当に通る / `requireTenantWideOrNodeScopedManage` は `node_id === null` のときだけ
 * 全店舗要求)。画面単位で機械的に差し替えると**正当なボタンを消す機能退行**になる。
 *
 * ⚠️ `scope` が undefined の行はテナント全体スコープ相当として扱う (fail-open)。理由は
 * `hasNonSelfPermission` と同じで、`scope` は D-446 で追加された新しいフィールドであり、それ以前の
 * `/auth/me` 応答を保持したセッションで会社管理者からボタンが消える機能退行を避けるため。
 * 表示制御であり、過大に見せても押した時点でサーバーが 403 を返す。
 */
export const hasTenantWidePermission = (
  permissions: EffectivePermission[] | undefined,
  feature: string,
  action: PermissionAction,
  nonInheritingFeatures: string[] = DEFAULT_NON_INHERITING_FEATURES,
): boolean => {
  if (!permissions) return false;
  const matching = matchingPermissions(permissions, feature, action, nonInheritingFeatures);
  return matching.some((p) => p.scope === undefined || TENANT_WIDE_SCOPES.includes(p.scope));
};

/**
 * v0.4 (D-440/D-447 の一般化、休暇管理 TD-26 積み残し②レビュー指摘): feature に一致する
 * permission 行を持っていても、それが**全て self スコープのみ**なら false を返す
 * (「自分の◯◯」しか持たないユーザーを、他人のレコードを見る画面/ナビ項目から除外する)。
 *
 * ⚠️ `hasPermission()` は scope を見ないため、self スコープしか持たないユーザー (例: R-STAFF の
 * `employee_family:view:self`) にも feature+action 一致だけで true を返してしまう。この関数は
 * その穴を塞ぐための追加フィルタであり、`hasPermission()` の代わりではなく組み合わせて使うこと。
 *
 * 元々 `lib/navigation.ts` の `canShowNavEntry` (`requiresNonSelfScope`) にだけ実装されていたが、
 * `EmployeeDetailPage.tsx` (家族情報タブ) が同じロジックを独立に再実装しようとした際のレビューで
 * 「二重実装は §8-55/§8-149 型の同期漏れリスクを持つ」と指摘され、ここへ集約した。
 *
 * ⚠️ scope が undefined の行は「self ではない」として扱う (fail-open)。理由は `canShowNavEntry` と
 * 同じ: `scope` は D-446 で追加された新しいフィールドであり、古いレスポンスを保持したクライアントで
 * 突然項目が消える事故を避けるため。表示制御であり実際の認可は常にサーバー側で行われる。
 *
 * @param features 対象の feature 名 (複数指定可、`requiredAnyFeature` 相当)。
 *   一致する permission 行が1件も無い場合は「self スコープのみで塞がれている」わけではないため true
 *   (= 塞がない) を返す。実際に権限があるかどうかは呼び出し側が別途 `hasPermission()` で判定すること。
 */
export const hasNonSelfPermission = (
  permissions: EffectivePermission[] | undefined,
  features: string[],
): boolean => {
  if (features.length === 0) return true;
  const matching = (permissions ?? []).filter((p) => features.includes(p.feature) || p.feature === '*');
  return !(matching.length > 0 && matching.every((p) => p.scope === 'self'));
};

type AuthState = {
  user: CurrentUser | null;
  setUser: (user: CurrentUser | null) => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),
}));
